import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/types";
import { createOpenApiConfiguration } from "@/lib/utils";
import {
  fetchTenantAccount,
  type TenantWithRegion,
} from "@/lib/utils/tenant-account";
import { TenantApi } from "@/openapi/generated";

interface UseTenantGetPublicProps {
  tenantId: number;
}

/**
 * Fetches tenant by id without authentication (public endpoint).
 * Use when server did not pass tenantData (e.g. contact page without auth context).
 */
export const useTenantGetPublic = (
  { tenantId }: UseTenantGetPublicProps,
  options?: { enabled?: boolean },
) => {
  const isRestoring = useIsRestoring();
  const { data, isLoading, isSuccess, isError } = useQuery({
    queryKey: [QueryKeys.Tenant, tenantId, "public"],
    queryFn: ({ client, queryKey }) => {
      const service = new TenantApi(createOpenApiConfiguration());
      const previous = client.getQueryData<TenantWithRegion>(queryKey);
      return fetchTenantAccount(service, tenantId, previous);
    },
    enabled: options?.enabled !== false && !!tenantId && !isRestoring,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    isLoading,
    isSuccess,
    isError,
  };
};
